import { CompareFundReturnResult, CompareFundReturnValues, ChartDataPoint, RobotResult } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

function monthlyRateFromAnnual(annualDecimal: number): number {
  if (Math.abs(annualDecimal) < 1e-12) return 0;
  return Math.pow(1 + annualDecimal, 1 / 12) - 1;
}

function monthlyFeeFromAnnual(annualFeeDecimal: number): number {
  if (annualFeeDecimal <= 0) return 0;
  if (annualFeeDecimal >= 1) return 1;
  return 1 - Math.pow(1 - annualFeeDecimal, 1 / 12);
}

/** Closed-form future value: start amount + end-of-month deposits, monthly compounding. */
export function futureValue(
  startingAmount: number,
  monthlySavings: number,
  annualRateDecimal: number,
  years: number
): number {
  const periods = Math.max(0, Math.round(years * 12));
  if (periods === 0) return startingAmount;

  const i = monthlyRateFromAnnual(annualRateDecimal);
  if (Math.abs(i) < 1e-15) {
    return startingAmount + monthlySavings * periods;
  }

  const growth = Math.pow(1 + i, periods);
  const fvPv = startingAmount * growth;
  const fvPmt = monthlySavings * ((growth - 1) / i);
  return fvPv + fvPmt;
}

interface PathPoint {
  year: number;
  value: number;
}

interface SimulatedPath {
  yearly: PathPoint[];
  finalAmount: number;
  grossFinalAmount: number;
  netReturnPercent: number;
}

function simulateSimple(
  startingAmount: number,
  monthlySavings: number,
  expectedReturnPercent: number,
  feePercent: number,
  years: number
): SimulatedPath {
  const net = (expectedReturnPercent - feePercent) / 100;
  const gross = expectedReturnPercent / 100;
  const yearly: PathPoint[] = [];

  for (let year = 0; year <= years; year++) {
    yearly.push({
      year,
      value: futureValue(startingAmount, monthlySavings, net, year),
    });
  }

  return {
    yearly,
    finalAmount: futureValue(startingAmount, monthlySavings, net, years),
    grossFinalAmount: futureValue(startingAmount, monthlySavings, gross, years),
    netReturnPercent: expectedReturnPercent - feePercent,
  };
}

function simulateAdvanced(
  startingAmount: number,
  monthlySavings: number,
  expectedReturnPercent: number,
  feePercent: number,
  years: number
): SimulatedPath {
  const monthlyGross = monthlyRateFromAnnual(expectedReturnPercent / 100);
  const monthlyFee = monthlyFeeFromAnnual(feePercent / 100);
  const periods = Math.max(0, Math.round(years * 12));

  const run = (feeRate: number) => {
    let value = startingAmount;
    const yearly: PathPoint[] = [{ year: 0, value }];
    for (let month = 1; month <= periods; month++) {
      value *= 1 + monthlyGross;
      value -= value * feeRate;
      value += monthlySavings;
      if (month % 12 === 0) {
        yearly.push({ year: month / 12, value });
      }
    }
    return { yearly, finalAmount: value };
  };

  const netPath = run(monthlyFee);
  const grossPath = run(0);
  const effectiveMonthly = (1 + monthlyGross) * (1 - monthlyFee) - 1;
  const netReturnPercent = (Math.pow(1 + effectiveMonthly, 12) - 1) * 100;

  return {
    yearly: netPath.yearly,
    finalAmount: netPath.finalAmount,
    grossFinalAmount: grossPath.finalAmount,
    netReturnPercent,
  };
}

function toRobotResult(
  path: SimulatedPath,
  contributed: number
): RobotResult {
  return {
    finalAmount: path.finalAmount,
    contributed,
    totalReturn: path.finalAmount - contributed,
    feeImpact: path.grossFinalAmount - path.finalAmount,
    netReturnPercent: path.netReturnPercent,
  };
}

export function compareFundRobots(
  values: CompareFundReturnValues,
  t?: ToolsTranslations['compareFundReturn']
): CompareFundReturnResult {
  const {
    monthlySavings,
    startingAmount,
    years,
    mode,
    robotAReturn,
    robotAFee,
    robotBReturn,
    robotBFee,
  } = values;

  if (monthlySavings < 0) return { isValid: false, message: t?.invalid.monthlySavings, contributed: 0, robotA: emptyRobot(), robotB: emptyRobot(), difference: 0, winner: 'tie' };
  if (startingAmount < 0) return { isValid: false, message: t?.invalid.startingAmount, contributed: 0, robotA: emptyRobot(), robotB: emptyRobot(), difference: 0, winner: 'tie' };
  if (years < 1) return { isValid: false, message: t?.invalid.years, contributed: 0, robotA: emptyRobot(), robotB: emptyRobot(), difference: 0, winner: 'tie' };
  if (robotAReturn < 0 || robotBReturn < 0) return { isValid: false, message: t?.invalid.expectedReturn, contributed: 0, robotA: emptyRobot(), robotB: emptyRobot(), difference: 0, winner: 'tie' };
  if (robotAFee < 0 || robotBFee < 0) return { isValid: false, message: t?.invalid.fee, contributed: 0, robotA: emptyRobot(), robotB: emptyRobot(), difference: 0, winner: 'tie' };

  const simulate = mode === 'advanced' ? simulateAdvanced : simulateSimple;
  const contributed = startingAmount + monthlySavings * years * 12;

  const pathA = simulate(startingAmount, monthlySavings, robotAReturn, robotAFee, years);
  const pathB = simulate(startingAmount, monthlySavings, robotBReturn, robotBFee, years);

  const robotA = toRobotResult(pathA, contributed);
  const robotB = toRobotResult(pathB, contributed);
  const difference = robotA.finalAmount - robotB.finalAmount;

  let winner: 'a' | 'b' | 'tie' = 'tie';
  if (Math.abs(difference) < 0.5) winner = 'tie';
  else if (difference > 0) winner = 'a';
  else winner = 'b';

  return {
    isValid: true,
    contributed,
    robotA,
    robotB,
    difference,
    winner,
  };
}

export function generateChartData(
  values: CompareFundReturnValues,
  translations: ToolsTranslations['compareFundReturn']
): ChartDataPoint[] {
  if (values.years < 1) return [];

  const simulate = values.mode === 'advanced' ? simulateAdvanced : simulateSimple;
  const pathA = simulate(values.startingAmount, values.monthlySavings, values.robotAReturn, values.robotAFee, values.years);
  const pathB = simulate(values.startingAmount, values.monthlySavings, values.robotBReturn, values.robotBFee, values.years);

  const length = Math.min(pathA.yearly.length, pathB.yearly.length);
  return Array.from({ length }, (_, i) => ({
    year: `${pathA.yearly[i].year} ${translations.yearsSuffix}`,
    robotA: Math.round(pathA.yearly[i].value),
    robotB: Math.round(pathB.yearly[i].value),
  }));
}

function emptyRobot(): RobotResult {
  return {
    finalAmount: 0,
    contributed: 0,
    totalReturn: 0,
    feeImpact: 0,
    netReturnPercent: 0,
  };
}
